// src/pages/Community/Location/LocationDetail.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot, collection } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import ReactMarkdown from "react-markdown";
import { db } from "../../../firebase";
import PageWrapper from "../../../components/PageWrapper";
import "../css/LocationDetail.css";
import LocationEditModal from "../Location/LocationEditModal";
import { ReactionBar, Bookmark, PresenceBadge, Discussion, LikeBar } from "../components";

const DEFAULT_GAME_MAP_IMG = "/assets/gta6-map.jpg";

/* Minimal inline Feedback modal (swap with your shared <FeedbackModal /> if you have one) */
function LocationFeedbackModal({ open, onClose, locationDoc }) {
  const [text, setText] = useState("");

  if (!open) return null;
  return (
    <div className="locd-modal" role="dialog" aria-modal="true">
      <div className="locd-modal-card">
        <div className="locd-modal-head">
          <strong>Feedback for: {locationDoc?.title || "Location"}</strong>
          <button className="locd-action" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <textarea
          className="locd-modal-text"
          rows={6}
          placeholder="Share a correction, hint, or improvement…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="locd-modal-actions">
          <button className="locd-action" onClick={onClose}>
            Cancel
          </button>
          <button
            className="locd-action"
            onClick={() => {
              navigator.clipboard?.writeText(text).catch(() => {});
              setText("");
              onClose();
            }}
          >
            💬 Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LocationDetail() {
  const { id } = useParams();
  const location = useLocation();

  // --- state & hooks (always declared in same order) ---
  const [authUser, setAuthUser] = useState(null);
  const uid = authUser?.uid ?? null;
  const [editOpen, setEditOpen] = useState(false);
  const [loc, setLoc] = useState(undefined); // undefined = loading, null = not found
  const [linkCopied, setLinkCopied] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);

  // Immediate UI feedback for selected reaction (best-effort)
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  // presence / active viewers count
  const [activeViewers, setActiveViewers] = useState(0);

  // basePath/bookmarkKey MUST be declared unconditionally
  const basePath = useMemo(() => ["locations", id], [id]);
  const bookmarkKey = useMemo(() => `locations:${id}`, [id]);

  // owner check (can use loc safely)
  const isOwner = !!uid && (loc?.createdBy === uid || loc?.authorUid === uid);

  // auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setAuthUser(u || null));
    return () => unsub();
  }, []);

  // Firestore doc listener
  useEffect(() => {
    if (!id) return;
    const ref = doc(db, "locations", id);
    const unsub = onSnapshot(ref, (snap) => {
      setLoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [id]);

  // presence listener: count peers in presence/locations/entities/{id}/peers
  useEffect(() => {
    if (!id) {
      setActiveViewers(0);
      return;
    }
    const peersCol = collection(db, "presence", "locations", "entities", id, "peers");
    const unsub = onSnapshot(
      peersCol,
      (snap) => {
        // count documents (exclude expired peers if your presence system writes expiry metadata — this just counts docs)
        setActiveViewers(Math.max(0, snap.size));
      },
      () => {
        // on error, don't crash UI — set to 0
        setActiveViewers(0);
      }
    );
    return () => unsub();
  }, [id]);

  // share / copy
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname;
    try {
      if (navigator.share) {
        await navigator.share({ title: loc?.title ?? "Location", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch {
      // no-op
    }
  }, [loc?.title, location.pathname]);

  // Loading / Not found states
  if (loc === undefined) {
    return (
      <PageWrapper>
        <div className="locd-wrap">
          <div className="locd-top">
            <Link className="locd-back" to="/community/map_locations">
              ← Back to Locations
            </Link>
          </div>
          <div className="locd-skel">
            <div className="locd-skel-cover" />
            <div className="locd-skel-lines" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (loc === null) {
    return (
      <PageWrapper>
        <div className="locd-wrap">
          <div className="locd-top">
            <Link className="locd-back" to="/community/map_locations">
              ← Back to Locations
            </Link>
          </div>
          <div className="locd-empty">Location not found.</div>
        </div>
      </PageWrapper>
    );
  }

  // Derived values
  const mapSrc = loc.mapImage || DEFAULT_GAME_MAP_IMG;
  const xRaw = loc.coordsPct?.xPct;
  const yRaw = loc.coordsPct?.yPct;
  const x = typeof xRaw === "number" ? xRaw : Number(xRaw);
  const y = typeof yRaw === "number" ? yRaw : Number(yRaw);
  const hasPin = Number.isFinite(x) && Number.isFinite(y);

  const canEdit =
    !!uid &&
    (loc.createdBy === uid ||
      (Array.isArray(loc.allowEditBy) && loc.allowEditBy.includes(uid)) ||
      loc.isAdminEditable === true);

  return (
    <PageWrapper>
      <div className="locd-wrap">
        {/* Top row */}
        <div className="locd-detail-toprow">
          <Link
            className="locd-back"
            to="/community/map_locations"
            aria-label="Back to Locations"
          >
            ← Back to Locations
          </Link>

          <div className="locd-actions">
            <button className="locd-action" onClick={onShare}>
              {linkCopied ? "✓ Link copied" : "⤴ Share"}
            </button>
            <button className="locd-action" onClick={() => setFbOpen(true)}>
              💬 Feedback
            </button>
            {canEdit && (
              <button className="locd-action" onClick={() => setEditOpen(true)}>
                ✎ Edit
              </button>
            )}
          </div>
        </div>

        {/* Hero */}
        <div className="locd-hero">
          {loc.coverUrl ? (
            <img
              src={loc.coverUrl}
              className="locd-cover"
              alt={`${loc.title || "Location"} cover`}
            />
          ) : (
            <div className="locd-cover locd-cover--placeholder" aria-hidden>
              🗺️
            </div>
          )}
          <div className="locd-heroMeta">
            <span className="locd-badge">{loc.category || "Location"}</span>
            <span
              className={`locd-tag ${loc.isHidden ? "is-hidden" : "is-visible"}`}
            >
              {loc.isHidden ? "Hidden" : "Visible"}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="locd-header">
          <h1 className="locd-title">
            {loc.title || "Untitled location"}
            {isOwner && (
              <span
                className="locd-ownerMark"
                role="img"
                aria-label="You created this location"
                title="You created this location"
              >
                👑
              </span>
            )}
          </h1>

          {/* small reaction pill showing the local selection (best-effort) */}
          {selectedReactionFromBar && (
            <div className="locd-featured-reaction" aria-hidden>
              {(() => {
                const id = selectedReactionFromBar;
                const map = {
                  love: "❤️ Love",
                  fire: "🔥 Hot",
                  insightful: "🧠 Insightful",
                  cool: "🆒 Cool",
                  bug: "🐞 Issue",
                  respect: "🤝 Respect",
                  funny: "😂 Funny",
                  applause: "👏 Applause",
                };
                return map[id] || id;
              })()}
            </div>
          )}

          <div className="locd-sub">
            {loc.city && <span>{loc.city}</span>}
            {loc.region && <span> • {loc.region}</span>}
            {loc.difficultyToFind && <span> • {loc.difficultyToFind}</span>}
            {hasPin && <span> • {x.toFixed(2)}%, {y.toFixed(2)}%</span>}
          </div>
        </div>

        {/* Small engagement row (presence + bookmark) */}
        <div className="locd-engage">
          <PresenceBadge presenceKey="locations" entityId={id} />
          <Bookmark bookmarkKey={bookmarkKey} />
        </div>

        {/* Body: two-column grid */}
        <div className="locd-body">
          <div className="locd-colLeft">
            {loc.description && (
              <>
                <h3 className="locd-h3">Location Insight</h3>
                <div className="locd-markdown">
                  <ReactMarkdown
                    components={{
                      img: ({ node, ...props }) => (
                        <img {...props} loading="lazy" alt={props.alt || ""} />
                      ),
                      a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer" />
                      ),
                    }}
                  >
                    {loc.description}
                  </ReactMarkdown>
                </div>
              </>
            )}

            {(loc.accessReq || loc.lootHint) && (
              <div className="locd-infoGrid">
                {loc.accessReq && (
                  <div className="locd-info">
                    <div className="locd-infoLabel">Access Requirements</div>
                    <div className="locd-infoValue">{loc.accessReq}</div>
                  </div>
                )}
                {loc.lootHint && (
                  <div className="locd-info">
                    <div className="locd-infoLabel">Loot / Reward</div>
                    <div className="locd-infoValue">{loc.lootHint}</div>
                  </div>
                )}
              </div>
            )}

            {Array.isArray(loc.images) && loc.images.length > 0 && (
              <>
                <h3 className="locd-h3">Gallery</h3>
                <div className="locd-gallery">
                  {loc.images.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={loc.title ? `${loc.title} — gallery ${i + 1}` : ""}
                      loading="lazy"
                    />
                  ))}
                </div>
              </>
            )}

            {/* Tags (we'll render ReactionBar + Discussion *after* the two-column grid) */}
            {Array.isArray(loc.tags) && loc.tags.length > 0 && (
              <>
                <h3 className="locd-h3">Tags</h3>
                <div className="locd-tags">
                  {loc.tags.map((t, i) => (
                    <span key={i} className="locd-chip">
                      #{t}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="locd-colRight">
            <div className="locd-panel">
              <h3 className="locd-h3">Game Map</h3>
              <div className="locd-mapimg-wrap">
                <img className="locd-mapimg" src={mapSrc} alt="Game map" />
                {hasPin && (
                  <div
                    className="locd-mappin"
                    style={{ left: `${x}%`, top: `${y}%` }}
                    title={loc.shortNote || loc.title || "Location"}
                  >
                    <span className="locd-mappin-dot" />
                    <span className="locd-mappin-pulse" />
                  </div>
                )}
              </div>
              {hasPin ? (
                <div className="locd-maplegend">
                  <code>
                    {x.toFixed(2)}%, {y.toFixed(2)}%
                  </code>
                  {loc.shortNote ? (
                    <span className="locd-legend-note"> • {loc.shortNote}</span>
                  ) : null}
                </div>
              ) : (
                <div className="locd-maplegend locd-muted">
                  No pin placed for this location.
                </div>
              )}
            </div>

            <div className="locd-panel">
              <h3 className="locd-h3">Details</h3>
              <ul className="locd-detailList">
                {loc.category && (
                  <li>
                    <strong>Category:</strong> {loc.category}
                  </li>
                )}
                {loc.city && (
                  <li>
                    <strong>City:</strong> {loc.city}
                  </li>
                )}
                {loc.region && (
                  <li>
                    <strong>Region:</strong> {loc.region}
                  </li>
                )}
                <li>
                  <strong>Visibility:</strong> {loc.isHidden ? "Hidden" : "Visible"}
                </li>
                {loc.difficultyToFind && (
                  <li>
                    <strong>Difficulty:</strong> {loc.difficultyToFind}
                  </li>
                )}
                {loc.createdAt && (
                  <li>
                    <strong>Added:</strong>{" "}
                    {new Date(loc.createdAt.toMillis?.() ?? loc.createdAt).toLocaleDateString()}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        {/* end .locd-body (two-column) */}

                {/* Full-width Engagement + Discussion (matches MissionDetails) */}
        <div className="locd-engageWrap">
          {/* One row: likes + reactions + viewers */}
          <div className="locd-engageRow">
            <LikeBar basePath={basePath} />

            <ReactionBar
              basePath={basePath}
              visibleReactions={[
                "love",
                "fire",
                "insightful",
                "cool",
                "bug",
                "respect",
                "funny",
                "applause",
              ]}
              onSelect={(id) => {
                setSelectedReactionFromBar(id);
              }}
            />
          </div>

          {/* Discussion always below the row */}
          <Discussion basePath={basePath} authorId={loc.createdBy || loc.authorUid} />
        </div>


        {/* Feedback modal */}
        <LocationFeedbackModal open={fbOpen} onClose={() => setFbOpen(false)} locationDoc={loc} />

        <LocationEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          locationId={loc.id}
          user={authUser}
          role={authUser?.role}
        />
      </div>
    </PageWrapper>
  );
}
